import { useEffect, useState } from 'react';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Returns the canonical version key for a ver.json payload.
 * Prefers commit_sha (most specific), falls back to build_id, then tag_name.
 * Returns empty string when running locally without build env vars set.
 */
function getVersionKey(ver) {
  return ver?.commit_sha || ver?.build_id || ver?.tag_name || '';
}

async function fetchVersion() {
  try {
    const res = await fetch('/ver.json', { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Detects when a new app version has been deployed by polling /ver.json
 * every 5 minutes and comparing build identifiers.
 *
 * Returns { hasUpdate: boolean }.
 * No-ops in dev when build env vars are empty (version key is '').
 */
export function useAppVersion() {
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    let pollTimer = null;

    async function init() {
      const initial = await fetchVersion();
      const initialKey = getVersionKey(initial);

      // Skip if running locally without build env vars (all fields empty)
      if (!initialKey) return;

      pollTimer = setInterval(async () => {
        const current = await fetchVersion();
        const currentKey = getVersionKey(current);
        if (currentKey && currentKey !== initialKey) {
          setHasUpdate(true);
          clearInterval(pollTimer);
          pollTimer = null;
        }
      }, POLL_INTERVAL_MS);
    }

    init();

    return () => {
      clearInterval(pollTimer);
    };
  }, []);

  return { hasUpdate };
}
